import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import priceTagIcon from '@/assets/icons/price-tag.svg'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'

interface Props {
  subtotal: number
  total: number
  surchargeTotal: number
  grandTotal?: number
  fractionDigits?: number
  leadingContent?: ReactNode
  tradeDiscountContent?: ReactNode
  tradeDiscountAmount?: number
  tradeDiscountRowLabel?: string
  tradeDiscountLabel?: string
  onTradeDiscountClick?: () => void
  taxBreakdown?: Array<{
    label: string
    amount: number
  }>
}

// Figma orange recap block below line items and optional charges.
export function QuoteGrandTotalSummary({
  subtotal,
  total,
  surchargeTotal,
  grandTotal,
  fractionDigits = 2,
  leadingContent,
  tradeDiscountContent,
  tradeDiscountAmount = 0,
  tradeDiscountRowLabel,
  tradeDiscountLabel,
  onTradeDiscountClick,
  taxBreakdown = [],
}: Props) {
  const { t } = useTranslation('quotes')
  const fmt = (value: number) => formatCurrency(value, 'EUR', fractionDigits)
  const resolvedGrandTotal = grandTotal ?? total + surchargeTotal
  const totalExcludingVat =
    Math.round((subtotal + surchargeTotal - tradeDiscountAmount) * 100) / 100

  const rows = [
    {
      label: t('form.grandTotal.subtotalServices'),
      value: subtotal,
      labelClass: 'text-[#6c7285]',
      valueClass: 'text-[#171a20]',
    },
    ...(surchargeTotal > 0
      ? [
          {
            label: t('form.grandTotal.optionalFeesExcludingVat'),
            value: surchargeTotal,
            labelClass: 'text-[#6c7285]',
            valueClass: 'text-[#171a20]',
          },
        ]
      : []),
  ]

  return (
    <div className='border-t border-[#e7e9ef] bg-white px-6 py-6'>
      <div className='grid gap-8 lg:grid-cols-[minmax(0,1fr)_480px]'>
        <div className='space-y-3'>{leadingContent}</div>

        <div className='space-y-4'>
          {rows.map(row => (
            <div
              key={row.label}
              className='flex items-center justify-between gap-6 text-sm font-medium'
            >
              <span className={row.labelClass}>{row.label}</span>
              <span className={cn('text-right font-medium', row.valueClass)}>
                {fmt(row.value)}
              </span>
            </div>
          ))}

          {tradeDiscountContent ? (
            tradeDiscountContent
          ) : tradeDiscountLabel ? (
            <Button
              type='button'
              variant='outline'
              disabled={!onTradeDiscountClick}
              onClick={onTradeDiscountClick}
              className='h-10 rounded-xl border-dashed border-[#b0b5c3] bg-white py-2 px-3 text-xs font-medium text-[#171a20] opacity-100 disabled:cursor-default disabled:opacity-100'
            >
              <img
                src={priceTagIcon}
                alt=''
                width={16}
                height={16}
                className='mr-2 shrink-0'
              />
              {tradeDiscountLabel}
            </Button>
          ) : null}

          {tradeDiscountAmount > 0 ? (
            <div className='flex items-center justify-between gap-6 text-sm font-medium'>
              <span className='text-[#059669]'>
                {tradeDiscountRowLabel ?? t('form.tradeDiscountCard.title')}
              </span>
              <span className='text-right font-medium text-[#059669]'>
                -{fmt(tradeDiscountAmount)}
              </span>
            </div>
          ) : null}

          <div className='border-t border-[#e7e9ef] pt-4'>
            <div className='flex items-center justify-between gap-6 text-sm font-bold text-[#171a20]'>
              <span>{t('form.grandTotal.subtotal')}</span>
              <span className='text-right'>{fmt(totalExcludingVat)}</span>
            </div>
          </div>

          {taxBreakdown.map(row => (
            <div
              key={row.label}
              className='flex items-center justify-between gap-6 text-sm font-medium'
            >
              <span className='text-[#6c7285]'>{row.label}</span>
              <span className='text-right font-medium text-[#171a20]'>
                {fmt(row.amount)}
              </span>
            </div>
          ))}

          <div className='border-t-2 border-[#6c7285] pt-6'>
            <div className='flex items-center justify-between gap-6'>
              <span className='font-heading text-base font-semibold leading-[1.4] tracking-[-0.04em] text-[#171a20]'>
                {t('form.grandTotal.grandTotal')}
              </span>
              <span className='font-heading text-2xl font-semibold leading-[1.4] tracking-normal text-[#171a20]'>
                {fmt(resolvedGrandTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuoteGrandTotalSummary
